import { getPoolLimit, getPoolStakedValue } from "@services/index";
import { useCallback, useEffect, useState } from "react";
import { useInterval, useIdle } from "react-use";
import { useSelector } from "react-redux";
import { useReadContract } from "wagmi";
import useDappConfig from "./useDappConfig";
import useDappChain from "./useDappChain";
import { toFixedWithoutRounding } from "@constants/common";
import { formatUnits } from "viem";

type PoolLimitDataType = {
  poolLimit: number;
  usedValue: number;
  isLoading: boolean;
};

const POLLING_INTERVAL = 10000;

interface UsePoolDataProps {
  enabled: boolean;
  conversionRate: number;
}

const initialData = {
  poolLimit: 0,
  usedValue: 0,
};

const usePoolData = ({ enabled, conversionRate }: UsePoolDataProps) => {
  const isIdle = useIdle(150e3);
  const { config } = useDappConfig();
  const { isEthereumChain } = useDappChain();
  const { selectedERC20 } = useSelector((state: any) => state.user);

  const [data, setData] = useState<PoolLimitDataType>({
    ...initialData,
    isLoading: true,
  });

  const { data: nonEthereumPoolData, isFetching } = useReadContract({
    ...config.xtoken,
    functionName: "balanceOf",
    args: [config.interactionPools.lrtDepositPool.address],
    query: {
      enabled: !isEthereumChain,
      refetchInterval: 10_000,
    },
  });

  const fetchPoolData = useCallback(async () => {
    const [poolLimit, poolStakedValue] = await Promise.all([
      getPoolLimit(selectedERC20),
      getPoolStakedValue(selectedERC20),
    ]);

    setData({
      poolLimit: poolLimit.data.value as number,
      usedValue: poolStakedValue.data.totalStaked as number,
      isLoading: false,
    });
  }, [selectedERC20]);

  useEffect(() => {
    if (enabled) {
      fetchPoolData();
    } else {
      setData({
        ...initialData,
        isLoading: false,
      });
    }
  }, [fetchPoolData, enabled]);

  useInterval(() => {
    if (!isIdle && enabled) {
      fetchPoolData();
    }
  }, POLLING_INTERVAL);

  const convertedPoolData = nonEthereumPoolData
    ? toFixedWithoutRounding(
        (
          parseFloat(
            formatUnits(nonEthereumPoolData as bigint, config.xtoken.decimals)
          ) * conversionRate
        ).toFixed(config.xtoken.decimals),
        8
      )
    : 0;

  const formattedBalance: PoolLimitDataType =
    !isEthereumChain && nonEthereumPoolData
      ? {
          poolLimit: Number(convertedPoolData) || 0,
          usedValue: 0,
          isLoading: isFetching,
        }
      : { ...initialData, isLoading: isEthereumChain ? false : isFetching };

  return isEthereumChain ? data : formattedBalance;
};

export default usePoolData;
